import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import LanguageDropdown from "@/components/nav/LanguageDropdown";
import Logo from "@/components/Logo";
import DemoModeSwitcher from "@/components/admin/DemoModeSwitcher";
import {
  Home, Grid3x3, CalendarDays, LogOut, UserCircle2, LayoutDashboard,
} from "lucide-react";

// POST-DEPLOY-FIX-04 (Sub-fase A) — Sticky top header for AdminLayout.
//
// Why: the existing AdminLayout shows the user identity / language /
// logout only in the left sidebar. On desktop the sidebar is sticky and
// visible, but the user reported losing visual anchor when scrolling
// deep into long admin views (responses, marketplace, vendors…). On
// mobile the original top bar was NOT sticky at all, so language and
// logout literally scrolled off the screen.
//
// What this component adds:
//   • A bar that sits sticky inside the <main> column (so it sits
//     RIGHT of the sticky sidebar on desktop, and spans the full width
//     on mobile where the sidebar collapses into a horizontal pill row).
//   • Section title resolved from the current pathname → i18n key.
//   • Quick links: public Home + TEAM OS Hub + Mi calendario.
//     (Dashboard is also shown when the user is on a non-/admin route,
//     so an admin viewing /app/calendar can jump back to /admin.)
//   • Language dropdown, user identity badge, logout — duplicated from
//     the sidebar on purpose so they are reachable from any scroll
//     position and any viewport size.
//
// Z-index is below the public Navbar's z-40 (we use z-30) so dialogs and
// dropdowns (z-50) keep covering it. The Navbar is not rendered inside
// AdminLayout, so there's no overlap on admin routes.

const STAFF_ROLE_KEYS = new Set([
  "admin", "staff", "qa_manager", "qa_tester", "institutional_observer",
]);

const resolveIdentityRole = (user, t) => {
  if (!user) return "";
  if (STAFF_ROLE_KEYS.has(user.role)) {
    const translated = t(`roles.${user.role}`);
    if (translated && translated !== `roles.${user.role}`) return translated;
  }
  if (user.profile_key) {
    const translated = t(`profiles.${user.profile_key}`);
    if (translated && translated !== `profiles.${user.profile_key}`) return translated;
  }
  return user.role || "";
};

// Resolve the human label of the current admin route. We keep this map
// here (and not in i18n) because every entry already has a translation
// key registered for the sidebar nav — we just reuse it. Falls back to
// the generic "Admin Console" label so unknown routes never render as
// a raw path.
const SECTION_MAP = [
  { match: (p) => p === "/admin", key: "admin.stats" },
  { match: (p) => p.startsWith("/admin/stats-advanced"), key: "admin.statsAdvanced" },
  { match: (p) => p.startsWith("/admin/users"), key: "admin.users" },
  { match: (p) => p.startsWith("/admin/questionnaires"), key: "admin.questionnaires" },
  { match: (p) => p.startsWith("/admin/responses"), key: "admin.responses" },
  { match: (p) => p.startsWith("/admin/import"), key: "admin.import" },
  { match: (p) => p.startsWith("/admin/interviewer"), key: "admin.interviewer" },
  { match: (p) => p.startsWith("/admin/campaigns"), key: "admin.campaigns" },
  { match: (p) => p.startsWith("/admin/consent"), key: "admin.consent" },
  { match: (p) => p.startsWith("/admin/testers"), key: "admin.testers" },
  { match: (p) => p.startsWith("/admin/referrals"), i18n: "referrals.nav.adminReferrals" },
  { match: (p) => p.startsWith("/admin/referral-campaigns"), i18n: "referrals.adminCampaigns.navLabel" },
  { match: (p) => p.startsWith("/admin/ad-slots"), i18n: "referrals.adminAdSlots.navLabel" },
  { match: (p) => p.startsWith("/admin/marketplace-requests"), i18n: "adminMarketplace.navLabel" },
  { match: (p) => p.startsWith("/admin/vendors"), i18n: "adminVendors.navLabel" },
  { match: (p) => p.startsWith("/admin/offers"), i18n: "adminOffers.navLabel" },
  { match: (p) => p.startsWith("/admin/fraud"), i18n: "referrals.nav.adminFraud" },
  { match: (p) => p.startsWith("/admin/qa-commissions"), i18n: "referrals.nav.qaCommissions" },
  { match: (p) => p.startsWith("/admin/settings"), key: "admin.settings" },
];

const resolveSectionLabel = (pathname, t) => {
  const entry = SECTION_MAP.find((e) => e.match(pathname));
  if (!entry) return t("common.admin");
  return entry.i18n ? t(entry.i18n) : t(entry.key);
};

const AdminTopBar = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const onLogout = () => { logout(); navigate("/"); };

  const sectionLabel = resolveSectionLabel(location.pathname, t);
  const identityRole = resolveIdentityRole(user, t);

  return (
    <header
      className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200"
      data-testid="admin-topbar"
    >
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 lg:px-6 h-14">
        {/* LEFT — section title + quick links */}
        <div className="flex items-center gap-2 min-w-0">
          {/* Mobile-only logo (the sidebar that holds it is md:hidden). */}
          <div className="md:hidden shrink-0" data-testid="admin-topbar-logo-mobile">
            <Logo />
          </div>
          <div
            className="hidden sm:block text-xs uppercase tracking-wide text-slate-400 shrink-0"
            data-testid="admin-topbar-crumb"
          >
            {t("common.admin")} /
          </div>
          <div
            className="font-semibold text-slate-900 text-sm sm:text-base truncate"
            data-testid="admin-topbar-section"
            title={sectionLabel}
          >
            {sectionLabel}
          </div>
        </div>

        {/* RIGHT — quick links + lang + identity + logout */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <NavLink
            to="/"
            className="hidden sm:inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            data-testid="admin-topbar-home"
            title={t("common.home")}
          >
            <Home className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">{t("common.home")}</span>
          </NavLink>
          <NavLink
            to="/apps"
            className="hidden sm:inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            data-testid="admin-topbar-hub"
            title="TEAM OS Hub"
          >
            <Grid3x3 className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">{t("apps.heroBadge")}</span>
          </NavLink>
          <NavLink
            to="/app"
            className="hidden md:inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            data-testid="admin-topbar-dashboard"
            title={t("common.dashboard")}
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">{t("common.dashboard")}</span>
          </NavLink>
          <NavLink
            to="/app/calendar"
            className="hidden md:inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100"
            data-testid="admin-topbar-calendar"
            title={t("calendar.title")}
          >
            <CalendarDays className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">{t("calendar.title")}</span>
          </NavLink>

          <div className="border-l border-slate-200 h-6 mx-1 hidden sm:block" aria-hidden />

          {/* D1 — Demo Preview switcher. Renders nothing for non-admins
              or when the feature flag is OFF, so safe to mount here. */}
          <DemoModeSwitcher />

          <div data-testid="admin-topbar-lang">
            <LanguageDropdown variant="navbar" testIdPrefix="admin-topbar-lang" />
          </div>

          {user && (
            <div
              className="hidden md:flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 max-w-[220px]"
              data-testid="admin-topbar-user"
              title={`${user.full_name || user.email} — ${identityRole}`}
            >
              <UserCircle2 className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
              <span className="truncate" data-testid="admin-topbar-user-name">
                {user.full_name || user.email}
              </span>
              {identityRole && (
                <>
                  <span className="text-slate-300">·</span>
                  <span
                    className="text-emerald-700 whitespace-nowrap"
                    data-testid="admin-topbar-user-role"
                  >
                    {identityRole}
                  </span>
                </>
              )}
            </div>
          )}

          <button
            onClick={onLogout}
            className="tsw-btn-logout text-xs px-3 py-1.5"
            data-testid="admin-topbar-logout"
            title={t("common.logout")}
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t("common.logout")}</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default AdminTopBar;
